import { CSSProperties, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { ApiError } from "@/api/client";
import {
  TemplateArt,
  TemplateMeta,
  getMyTemplate,
  listMyTemplates,
  putMyTemplate,
  resetMyTemplate,
} from "@/api/settings";

const TRIGGER: Record<TemplateArt, string> = {
  buchungsbestaetigung: "Manuell · per E-Mail-Entwurf zu einer Buchung",
  infos_gruppe: "Manuell · vor Anreise an die Gruppe",
  rechnung: "Manuell · nach Abreise mit Rechnung",
  konus_link: "Manuell · Kurtaxe-Daten-Anforderung mit personalem Link",
};

const PLATZHALTER_COMMON = [
  "{gast_oder_gruppenname}", "{gastname}", "{gruppenname}",
  "{anrede}", "{vorname}", "{nachname}",
  "{email}", "{telefon}",
  "{check_in}", "{check_out}", "{check_in_time}", "{check_out_time}",
  "{buchungsnummer}", "{buchungsreferenz}", "{buchungsdatum}",
  "{einheit}", "{einheit_kurz}", "{einheit_lang}",
  "{gaesteanzahl}", "{erwachsene}", "{kinder}",
  "{personen_ab_16}", "{kinder_6_bis_15}", "{kinder_unter_6}",
  "{gesamtpreis}", "{status}", "{heute}",
];

const PLATZHALTER_RECHNUNG = [
  "{rechnungsnummer}", "{rechnungsdatum}", "{rechnungsbetrag}",
  "{storno_gebuehr}", "{storno_regel_hinweis}", "{storniert_am}",
];

const PLATZHALTER: Record<TemplateArt, string[]> = {
  buchungsbestaetigung: [...PLATZHALTER_COMMON, "{buchungsuebersicht}", "{dokument_satz}"],
  infos_gruppe: [...PLATZHALTER_COMMON, "{buchungsuebersicht}", "{schluesselcode}", "{dokument_satz}"],
  rechnung: [...PLATZHALTER_COMMON, "{buchungsuebersicht}", ...PLATZHALTER_RECHNUNG, "{dokument_satz}"],
  konus_link: [...PLATZHALTER_COMMON, "{anrede_text}", "{konus_link}"],
};

const MD_CODE: CSSProperties = {
  background: "var(--bg-subtle)",
  padding: "1px 4px",
  borderRadius: 4,
};

export function VorlagenSection() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<TemplateArt>("buchungsbestaetigung");
  const [betreff, setBetreff] = useState("");
  const [inhalt, setInhalt] = useState("");
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const list = useQuery({ queryKey: ["my-templates"], queryFn: listMyTemplates });
  const tpl = useQuery({
    queryKey: ["my-template", selected],
    queryFn: () => getMyTemplate(selected),
  });

  useEffect(() => {
    if (tpl.data) {
      setBetreff(tpl.data.betreff);
      setInhalt(tpl.data.inhalt);
      setDirty(false);
      setMsg(null);
    }
  }, [tpl.data?.art, tpl.data?.betreff, tpl.data?.inhalt]);

  const saveMut = useMutation({
    mutationFn: () => putMyTemplate(selected, betreff, inhalt),
    onSuccess: async () => {
      setDirty(false);
      setMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["my-templates"] });
      await qc.invalidateQueries({ queryKey: ["my-template", selected] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  const resetMut = useMutation({
    mutationFn: () => resetMyTemplate(selected),
    onSuccess: async () => {
      setMsg("Auf Standard zurückgesetzt.");
      await qc.invalidateQueries({ queryKey: ["my-templates"] });
      await qc.invalidateQueries({ queryKey: ["my-template", selected] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Zurücksetzen fehlgeschlagen.");
    },
  });

  const items: TemplateMeta[] = list.data ?? [
    { art: "buchungsbestaetigung", typ: "", label: "Buchungsbestätigung", is_user_override: false },
    { art: "infos_gruppe", typ: "", label: "Infos an Gruppe", is_user_override: false },
    { art: "rechnung", typ: "", label: "Rechnung", is_user_override: false },
    { art: "konus_link", typ: "", label: "Kurtaxe-Daten-Anforderung", is_user_override: false },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
      <Card padded={false}>
        <div
          style={{
            padding: "12px 14px",
            borderBottom: "1px solid var(--border)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Vorlagen
        </div>
        {items.map((t, i) => {
          const active = selected === t.art;
          return (
            <button
              key={t.art}
              type="button"
              onClick={() => setSelected(t.art)}
              style={{
                width: "100%",
                display: "block",
                textAlign: "left",
                padding: "10px 14px",
                border: "none",
                borderBottom: i < items.length - 1 ? "1px solid var(--border)" : "none",
                background: active ? "var(--bg-selected)" : "transparent",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--brand-900)" : "var(--fg)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {t.label}
                {t.is_user_override && (
                  <span
                    title="Sie haben hier eine eigene Vorlage hinterlegt"
                    style={{
                      fontSize: 10,
                      padding: "1px 6px",
                      borderRadius: 999,
                      background: "var(--brand-100)",
                      color: "var(--brand-900)",
                      fontWeight: 600,
                    }}
                  >
                    eigen
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>
                {TRIGGER[t.art]}
              </div>
            </button>
          );
        })}
      </Card>

      <Card>
        <SectionHeader
          title={items.find((i) => i.art === selected)?.label || "Vorlage"}
          desc={TRIGGER[selected]}
          actions={
            <>
              {tpl.data?.is_user_override && (
                <Btn
                  variant="ghost"
                  icon="restart_alt"
                  size="sm"
                  onClick={() => resetMut.mutate()}
                  disabled={resetMut.isPending}
                >
                  Auf Standard zurücksetzen
                </Btn>
              )}
              <Btn
                variant="primary"
                icon="check"
                size="sm"
                onClick={() => saveMut.mutate()}
                disabled={!dirty || saveMut.isPending}
              >
                Speichern
              </Btn>
            </>
          }
        />
        <SettingsRow label="Betreff">
          <input
            style={S_INPUT}
            value={betreff}
            onChange={(e) => {
              setBetreff(e.target.value);
              setDirty(true);
            }}
          />
        </SettingsRow>
        <SettingsRow
          label="Inhalt"
          hint="Platzhalter werden beim Versand ersetzt."
          align="flex-start"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea
              style={{
                ...S_INPUT,
                maxWidth: "100%",
                minHeight: 260,
                resize: "vertical",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                lineHeight: 1.5,
              }}
              value={inhalt}
              onChange={(e) => {
                setInhalt(e.target.value);
                setDirty(true);
              }}
            />
            <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
              Platzhalter:{" "}
              {(PLATZHALTER[selected] ?? []).map((p, i, arr) => (
                <span key={p}>
                  <code style={{ background: "var(--bg-subtle)", padding: "1px 4px", borderRadius: 4 }}>
                    {p}
                  </code>
                  {i < arr.length - 1 ? " · " : ""}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", lineHeight: 1.7 }}>
              <strong>Formatierung (Markdown):</strong>{" "}
              <code style={MD_CODE}>**fett**</code> ·{" "}
              <code style={MD_CODE}>*kursiv*</code> ·{" "}
              <code style={MD_CODE}>- Aufzählung</code> ·{" "}
              <code style={MD_CODE}>1. Nummerierung</code> ·{" "}
              <code style={MD_CODE}># Überschrift</code> ·{" "}
              <code style={MD_CODE}>[Text](https://…)</code> · eine Leerzeile = neuer Absatz.
              {" "}HTML wird nicht interpretiert (sicher).
            </div>
            {msg && <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>{msg}</div>}
          </div>
        </SettingsRow>
      </Card>
    </div>
  );
}
